import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import ContactForm from '@/components/sections/ContactForm';
import { SERVICES_FR, SERVICES_EN, CITIES, SITE_CONFIG } from '@/data/siteData';
import { Calendar, Clock, User, ArrowLeft, Phone, Share2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const prefix = lang === 'en' ? '/en' : '';
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}?lang=${lang}`);
        setArticle(response.data);
        
        // Fetch related articles
        const relatedResponse = await axios.get(`${BACKEND_URL}/api/blog?lang=${lang}&limit=3&exclude=${slug}`);
        setRelatedArticles(relatedResponse.data);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, lang]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-800 mb-4">
              {t('Article non trouvé', 'Article not found')}
            </h1>
            <Link to={`${prefix}/blog`} className="text-amber-600 hover:text-amber-700">
              {t('Retour au blog', 'Back to blog')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{article.title} | VS Riviera Conciergerie</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`${window.location.origin}${prefix}/blog/${slug}`} />
      </Helmet>

      <Header />
      
      <main data-testid={`blog-article-${slug}`}>
        {/* Article Header */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link 
              to={`${prefix}/blog`}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('Retour au blog', 'Back to blog')}
            </Link>

            {/* Category */}
            <span className="inline-block bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
              {article.category}
            </span>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-slate-300">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(article.date).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {article.readTime} min {t('de lecture', 'read')}
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <article 
                  className="prose prose-slate prose-lg max-w-none prose-headings:text-slate-800 prose-a:text-amber-600 prose-a:no-underline hover:prose-a:underline"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Share */}
                <div className="mt-12 pt-8 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800">
                      {t('Partager cet article', 'Share this article')}
                    </span>
                    <button 
                      onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
                      className="flex items-center gap-2 text-amber-600 hover:text-amber-700"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                {/* CTA Box */}
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white mb-8 sticky top-24">
                  <h3 className="font-bold text-lg mb-3">
                    {t('Besoin d\'un devis ?', 'Need a quote?')}
                  </h3>
                  <p className="text-amber-100 text-sm mb-4">
                    {t(
                      'Contactez-nous pour une estimation gratuite de nos services.',
                      'Contact us for a free estimate of our services.'
                    )}
                  </p>
                  <a 
                    href={SITE_CONFIG.phoneLink}
                    className="flex items-center justify-center gap-2 bg-white text-amber-600 px-4 py-3 rounded-full font-semibold text-sm hover:bg-amber-50 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {SITE_CONFIG.phone}
                  </a>
                </div>

                {/* Services Links */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-bold text-slate-800 mb-4">
                    {t('Nos Services', 'Our Services')}
                  </h3>
                  <div className="space-y-2">
                    {services.slice(0, 4).map((service) => (
                      <Link
                        key={service.id}
                        to={`${prefix}/services/${service.slug}`}
                        className="block text-slate-600 hover:text-amber-600 text-sm py-1 transition-colors"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-8">
                {t('Articles similaires', 'Related articles')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`${prefix}/blog/${related.slug}`}
                    className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all"
                  >
                    <span className="text-sm text-amber-600 font-medium">{related.category}</span>
                    <h3 className="font-bold text-slate-800 mt-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-slate-600 text-sm mt-2 line-clamp-2">{related.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <ContactForm />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default BlogArticlePage;
